import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { FaPlus, FaMinus } from "react-icons/fa";
import Select from "react-select";
import { BASE_URL, SCRAPING_BASE_URL } from "../utils";

/** 🔹 AG Grid */
import AppDataGrid from "./AppDataGrid";
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";
ModuleRegistry.registerModules([AllCommunityModule, MasterDetailModule]);

export default function CheckCaseStatusPage() {
  const [data, setData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [allCaseOptions, setAllCaseOptions] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [checkDate, setCheckDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const gridRef = useRef(null);

  /* ---------- width map ---------- */
  const colWidth = (field) => {
    if (field === "CaseID") return 100;
    if (field === "petition_date") return 110;
    if (field === "status") return 150;
    if (field === "sub_status") return 100;
    if (field === "pdf_filename") return 250;
    if (field === "petition_court") return 200;
    if (field === "petition_type") return 200;
    return 120;
  };

  const fetchCaseStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/case/case-ids-data`);
      setData(res.data);
    } catch (err) {
      console.error("❌ Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/case/all-ids`)
      .then((res) => {
        const options = Array.isArray(res.data)
          ? res.data.map((caseId) => ({ label: caseId, value: caseId }))
          : [];
        setAllCaseOptions(options);
      })
      .catch((err) => console.error("❌ Failed to fetch case IDs:", err));

    fetchCaseStatus();
  }, [results]);

  const handleCheck = async () => {
    if (!selectedCourt || selectedCases.length === 0) {
      alert("⚠️ Please select both Court and Case IDs before checking.");
      return;
    }
    try {
      setLoading(true);
      let formattedDate = checkDate;
      if (checkDate.includes("-")) {
        const [year, month, day] = checkDate.split("-");
        formattedDate = `${month}/${day}/${year}`;
      }
      const response = await axios.post(`${SCRAPING_BASE_URL}/api/refreshCaseID`, {
        HARDCODED_DOCUMENT_CHECK_DATE_STR: formattedDate,
        KINGS_COUNTY_TEXT: selectedCourt,
        TARGET_FILE_NUMBERS: selectedCases.map((c) => c.value),
      });
      setResults(response.data);
      window.location.reload();
    } catch (err) {
      console.error("❌ API Error:", err);
      alert("Something went wrong while fetching data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter((row) =>
    Object.values(row).some(
      (val) => typeof val === "string" && val.toLowerCase().includes(globalFilter.toLowerCase())
    )
  );

  const skeletonRows = Array.from({ length: 10 }).map((_, idx) => ({
    case_id: `loading-${idx}`,
    documents: [],
    created_at: "",
    updated_at: "",
    properties: [],
  }));

  /* ---------- Detail Renderer themed ---------- */
  const DetailRenderer = (props) => {
    const row = props.data;
    return (
      <div
        className="p-4 border-t"
        style={{
          whiteSpace: "normal",
          background: "var(--surface)",
          color: "var(--fg)",
          borderColor: "var(--border)",
        }}
      >
        <h4 className="text-xs font-semibold mb-2 text-[var(--fg)]">Property Details:</h4>

        <div className="overflow-auto rounded-lg border border-[var(--border)]">
          <table className="table-auto text-xs w-full">
            <thead style={{ background: "var(--surface-muted)" }}>
              <tr>
                <th className="px-2 py-2 text-left text-[var(--fg)]">Party Name</th>
                <th className="px-2 py-2 text-left text-[var(--fg)]">Role</th>
                <th className="px-2 py-2 text-left text-[var(--fg)]">DOD</th>
                <th className="px-2 py-2 text-left text-[var(--fg)]">Appointed</th>
                <th className="px-2 py-2 text-left text-[var(--fg)]">Active</th>
              </tr>
            </thead>

            <tbody>
              {(row.properties || []).map((p, idx) => (
                <tr
                  key={idx}
                  style={{
                    background: idx % 2 === 0 ? "var(--surface)" : "var(--surface-muted)",
                    borderTop: `1px solid var(--border)`,
                  }}
                >
                  <td className="px-2 py-2">{p.party_name}</td>
                  <td className="px-2 py-2">
                    {String(p.role || "").toUpperCase() === "ADMINISTRATOR" ? (
                      <span className="badge-pill badge-green">{p.role}</span>
                    ) : (
                      p.role || "—"
                    )}
                  </td>
                  <td className="px-2 py-2">{p.dod || "—"}</td>
                  <td className="px-2 py-2">{p.appointed || "—"}</td>
                  <td className="px-2 py-2">{p.active || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const toggleDetail = (params) => {
    const api = params.api;
    const node = params.node;
    const id = node?.data?.case_id;
    const wasExpanded = !!node.expanded;

    api.forEachNode((n) => n.setExpanded(false));
    if (!wasExpanded) {
      node.setExpanded(true);
      setExpandedRow(id);
    } else {
      setExpandedRow(null);
    }
    api.refreshCells({ force: true });
    setTimeout(() => api.resetRowHeights(), 0);
  };

  /* ---------- Column defs ---------- */
  const columnDefs = [
    {
      colId: "case_id",
      field: "case_id",
      headerName: "Case ID",
      sortable: true,
      filter: "agTextColumnFilter",
      width: colWidth("CaseID"),
      cellRenderer: (params) => {
        const row = params.data;
        const id = row?.CaseID ?? row?.case_id ?? "";
        if (typeof id === "string" && id.startsWith("loading-")) {
          return <div className="h-3 w-24 rounded animate-pulse" style={{ background: "var(--surface-muted)" }} />;
        }
        const court = row?.petition_court ?? "";
        const type = row?.petition_type ?? "";
        const href = `${window.location.origin}/websurrogate/case-details/${encodeURIComponent(
          id
        )}/${encodeURIComponent(court)}/${encodeURIComponent(type)}`;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--accent)] no-underline text-xs hover:underline"
            title={href}
          >
            {id}
          </a>
        );
      },
    },
    {
      colId: "properties",
      headerName: "Properties",
      width: colWidth("sub_status"),
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        const isExpanded = params.node.expanded;
        const hasAdministrator = Array.isArray(row.properties)
          ? row.properties.some((p) => p.role?.toUpperCase() === "ADMINISTRATOR")
          : false;

        if (row.case_id?.startsWith("loading-")) {
          return <div className="h-4 w-20 rounded animate-pulse" style={{ background: "var(--surface-muted)" }} />;
        }

        // themed colors for icon button
        const bg = isExpanded
          ? "bg-rose-600 hover:bg-rose-700"
          : hasAdministrator
          ? "bg-emerald-600 hover:bg-emerald-700"
          : "bg-[var(--accent)] hover:bg-[var(--accent-strong)]";

        return (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleDetail(params);
              }}
              className={`w-5 h-5 rounded-full flex items-center justify-center shadow-sm border border-[var(--border)] text-white transition ${bg}`}
              title={isExpanded ? "Collapse" : "Expand"}
              type="button"
            >
              {isExpanded ? <FaMinus size={10} /> : <FaPlus size={10} />}
            </button>
            <span className="text-xs text-[var(--fg-muted)]">View</span>
          </div>
        );
      },
    },
    {
      colId: "documents",
      field: "documents",
      headerName: "Documents",
      sortable: false,
      filter: false,
      flex: 1,
      minWidth: colWidth("pdf_filename"),
      wrapText: true,
      autoHeight: true,
      cellStyle: { lineHeight: "16px", whiteSpace: "normal" },
      cellRenderer: (params) => {
        const row = params.data;
        if (row.case_id?.startsWith("loading-")) {
          return <div className="h-3 w-32 rounded animate-pulse" style={{ background: "var(--surface-muted)" }} />;
        }
        if (!row.documents || row.documents.length === 0) return "—";
        return (
          <span className="text-xs">
            {row.documents.map((doc, i) => {
              const match = doc.file_name?.match(/^(.+?)_File_No_.*_DocFiled_(\d{2}_\d{2}_\d{4})\.pdf$/);
              const label = match ? `${match[1]} (${match[2]})` : doc.file_name;
              return (
                <span key={i}>
                  <a
                    href={`${BASE_URL}${doc.url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[var(--accent)] no-underline text-xs hover:underline transition-colors duration-150"
                  >
                    {label}
                  </a>
                  {i < row.documents.length - 1 && <> <strong> || </strong> </>}
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      colId: "petition_court",
      headerName: "Petition Court",
      field: "petition_court",
      width: colWidth("petition_court"),
      sortable: true,
      filter: "agTextColumnFilter",
      valueGetter: (p) => p.data?.petition_court ?? "",
      cellRenderer: (p) => {
        const text = p.value || "";
        const shown = text.length > 60 ? text.slice(0, 60) + "…" : text;
        return (
          <span className="text-xs" title={text}>
            {shown}
          </span>
        );
      },
    },
    {
      colId: "petition_type",
      headerName: "Petition Type",
      field: "petition_type",
      width: colWidth("petition_type"),
      sortable: true,
      filter: "agTextColumnFilter",
      valueGetter: (p) => p.data?.petition_type ?? "",
      cellRenderer: (p) => {
        const text = p.value || "";
        const shown = text.length > 60 ? text.slice(0, 60) + "…" : text;
        return (
          <span className="text-xs" title={text}>
            {shown}
          </span>
        );
      },
    },
    {
      colId: "created_at",
      field: "created_at",
      headerName: "Created",
      sortable: true,
      filter: "agTextColumnFilter",
      width: colWidth("petition_date"),
      valueGetter: (params) => params.data?.created_at || "",
      cellRenderer: (params) => {
        const row = params.data;
        if (row.case_id?.startsWith("loading-")) {
          return <div className="h-3 w-20 rounded animate-pulse" style={{ background: "var(--surface-muted)" }} />;
        }
        return new Date(row.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "2-digit",
        });
      },
    },
    {
      colId: "updated_at",
      field: "updated_at",
      headerName: "Updated",
      sortable: true,
      filter: "agTextColumnFilter",
      width: colWidth("updated_date"),
      valueGetter: (params) => params.data?.updated_at || "",
      cellRenderer: (params) => {
        const row = params.data;
        if (row.case_id?.startsWith("loading-")) {
          return <div className="h-3 w-20 rounded animate-pulse" style={{ background: "var(--surface-muted)" }} />;
        }
        return new Date(row.updated_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "2-digit",
        });
      },
    },
  ];

  /* Fit columns to container but lock mapped widths */
  const sizeToFit = (api) => {
    if (!api) return;
    api.sizeColumnsToFit({
      defaultMinWidth: 100,
      columnLimits: [
        { key: "case_id", minWidth: colWidth("CaseID"), maxWidth: colWidth("CaseID") },
        { key: "petition_court", minWidth: colWidth("petition_court"), maxWidth: colWidth("petition_court") },
        { key: "petition_type", minWidth: colWidth("petition_type"), maxWidth: colWidth("petition_type") },
        { key: "properties", minWidth: colWidth("sub_status"), maxWidth: colWidth("sub_status") },
        { key: "documents", minWidth: colWidth("pdf_filename") },
        { key: "created_at", minWidth: colWidth("petition_date"), maxWidth: colWidth("petition_date") },
        { key: "updated_at", minWidth: colWidth("updated_date"), maxWidth: colWidth("updated_date") },
      ],
    });
  };

  useEffect(() => {
    const onResize = () => sizeToFit(gridRef.current?.api);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ---------- react-select themed styles ---------- */
  const selectStyles = useMemo(() => {
    const v = (name, fallback) => {
      try {
        const val = getComputedStyle(document.documentElement).getPropertyValue(name);
        return (val || "").trim() || fallback;
      } catch {
        return fallback;
      }
    };

    const surface = v("--surface", "#ffffff");
    const surfaceMuted = v("--surface-muted", "#f1f5f9");
    const fg = v("--fg", "#0f172a");
    const fgMuted = v("--fg-muted", "#64748b");
    const border = v("--border", "#e2e8f0");
    const accent = v("--accent", "#2563eb");

    return {
      control: (base, state) => ({
        ...base,
        minHeight: 34,
        height: 34,
        
        fontSize: 13,
        backgroundColor: surface,
        borderColor: state.isFocused ? accent : border,
        boxShadow: state.isFocused ? `0 0 0 2px rgba(96,165,250,0.25)` : "none",
        color: fg,
      }),
      singleValue: (base) => ({ ...base, color: fg }),
      placeholder: (base) => ({ ...base, color: fgMuted }),
      input: (base) => ({ ...base, color: fg, margin: 0, padding: 0 }),
      option: (base, { isFocused, isSelected }) => ({
        ...base,
        fontSize: 13,
        backgroundColor: isSelected ? surfaceMuted : isFocused ? surfaceMuted : surface,
        color: fg,
        padding: "8px 10px",
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
        
        backgroundColor: surface,
        border: `1px solid ${border}`,
        boxShadow: "0 18px 60px rgba(0,0,0,0.18)",
        borderRadius: 12,
        overflow: "hidden",
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      valueContainer: (base) => ({
        ...base,
        maxHeight: 84,
        overflowY: "auto",
        padding: "0 6px",
        flexWrap: "wrap",
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: surfaceMuted,
        borderRadius: 9999,
        border: `1px solid ${border}`,
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: fg,
        fontWeight: 600,
        fontSize: 12,
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: fgMuted,
        ":hover": { backgroundColor: "transparent", color: fg },
      }),
      indicatorsContainer: (base) => ({ ...base, height: 34 }),
      dropdownIndicator: (base) => ({ ...base, color: fgMuted, ":hover": { color: fg } }),
      clearIndicator: (base) => ({ ...base, color: fgMuted, ":hover": { color: fg } }),
    };
  }, []);

  const SELECT_ALL_OPTION = { label: "Select All", value: "__all__" };

  return (
    <div className="p-3">
      <div className="relative">
        {loading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="surface-card rounded-2xl p-5 flex flex-col items-center">
              <div
                className="h-10 w-10 border-4 border-t-transparent rounded-full animate-spin mb-2"
                style={{ borderColor: "var(--accent)", borderTopColor: "transparent" }}
              />
              <p className="text-sm font-semibold text-[var(--fg)]">Loading, please wait...</p>
              <p className="text-xs text-[var(--fg-muted)] mt-1">Fetching latest case updates</p>
            </div>
          </div>
        )}

        <h2 className="text-xs font-semibold mb-2 text-[var(--fg)]">
  Check Case Status
</h2>

{/* Controls */}
<div className="glass-panel rounded-xl p-2 border border-[var(--border)] mb-2">
  {/* Row 1 */}
  <div className="grid grid-cols-12 gap-2 items-center">
    <div className="col-span-12 md:col-span-3">
      <select
        value={selectedCourt}
        onChange={(e) => setSelectedCourt(e.target.value)}
        className="field h-8 px-2 text-[11px] rounded w-full"
      >
        <option value="">Select Court</option>
        <option value="Queens County Surrogate's Court">Queens court</option>
        <option value="Kings County Surrogate's Court">Kings Court</option>
        <option value="Bronx County Surrogate's Court">Bronx Court</option>
      </select>
    </div>

    <div className="col-span-12 md:col-span-2">
      <input
        type="date"
        value={checkDate}
        onChange={(e) => setCheckDate(e.target.value)}
        className="field h-8 px-2 text-[11px] rounded w-full"
      />
    </div>

    <div className="col-span-12 md:col-span-5">
      <Select
        options={[SELECT_ALL_OPTION, ...allCaseOptions]}
        isMulti
        placeholder="Select Case IDs"
        value={selectedCases}
        onChange={(selected) => {
          if (selected.some((opt) => opt.value === "__all__")) {
            const allSelected = selectedCases.length === allCaseOptions.length;
            setSelectedCases(allSelected ? [] : allCaseOptions);
          } else {
            setSelectedCases(selected);
          }
        }}
        isSearchable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        menuPortalTarget={document.body}
        styles={{
          ...selectStyles,
          control: (base, state) => ({
            ...selectStyles.control(base, state),
            width: "100%",
            minHeight: 32,
            height: 32,
            fontSize: "11px",
          }),
          valueContainer: (base) => ({
            ...base,
            paddingTop: 0,
            paddingBottom: 0,
          }),
          indicatorsContainer: (base) => ({
            ...base,
            height: 32,
          }),
          menu: (base) => ({
            ...selectStyles.menu(base),
            width: "100%",
            fontSize: "12px",
          }),
          multiValue: (base) => ({
            ...base,
            fontSize: "11px",
          }),
          multiValueLabel: (base) => ({
            ...base,
            fontSize: "11px",
            padding: "1px 6px",
          }),
          multiValueRemove: (base) => ({
            ...base,
            padding: "0 4px",
          }),
        }}
      />
    </div>

    <div className="col-span-12 md:col-span-2 flex md:justify-end">
      <button
        onClick={handleCheck}
        className="btn-primary text-[11px] h-8 px-3 w-full md:w-auto"
        disabled={loading}
        type="button"
      >
        {loading ? "Checking..." : "Check"}
      </button>
    </div>
  </div>

  {/* Row 2 */}
  <div className="grid grid-cols-12 gap-2 items-center mt-2">
    <div className="col-span-12 md:col-span-6">
      <input
        type="text"
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
        placeholder="🔍 Global search..."
        className="field h-8 px-2.5 text-[11px] rounded w-full"
      />
    </div>

    <div className="col-span-12 md:col-span-6 flex items-center md:justify-end gap-2">
      <span className="text-[11px] text-[var(--fg-muted)]">Rows:</span>
      <select
        value={perPage}
        onChange={(e) => setPerPage(Number(e.target.value))}
        className="field h-8 rounded px-2.5 text-[11px]"
      >
        {[10, 20, 50, 100].map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </div>
  </div>
</div>


        <div className="overflow-hidden border border-[var(--border)] rounded-2xl shadow-sm">
          <AppDataGrid
            gridRef={gridRef}
            rowData={loading ? skeletonRows : filteredData}
            columnDefs={columnDefs}
            height={"calc(100vh - 220px)"}
            minWidth={900}
            defaultColDef={{ resizable: false, cellClass: "text-xs" }}
            suppressCellFocus={true}
            rowHeight={42}
            headerHeight={36}
            masterDetail={true}
            isRowMaster={() => true}
            detailCellRenderer={DetailRenderer}
            detailRowAutoHeight={true}
            getRowId={(params) => params.data.case_id}
            pagination={true}
            paginationPageSize={perPage}
            animateRows={true}
            alwaysShowVerticalScroll={true}
            onFirstDataRendered={(params) => {
              params.api.forEachNode((n) => n.setExpanded(false));
              sizeToFit(params.api);
            }}
            onFilterChanged={(params) => params.api.resetRowHeights()}
            quickFilterText={globalFilter}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}
